import { Site } from '../../types/Site';
import { logError } from '../../utils/logger';

export interface NavigationLike {
  navigate: (routeName: string, params?: object) => void;
}

export type SiteDetailsDestination =
  | { kind: 'legacyPantry'; organizationId: string }
  | { kind: 'freshTrakSite'; siteId: string };

// Mirrors the legacy PantryMap.js's "view pantry details" behavior: anything that isn't a
// freshtrak site has a real record in the legacy pantry system, so it resolves to that record
// (keyed by provider.alternateIds.organizationId, the same organization id the legacy Pantry
// screen/PantryDetails component already expects). A freshtrak site -- or a site with no provider
// info at all -- has no legacy pantry record, so it resolves to the new, FreshTrak-aware site
// details instead. Shared by navigateToSiteDetails (narrow layouts, a real screen navigation) and
// SiteMapPantriesContent's wide-layout inline panel, so both pick the same destination the same
// way.
export function resolveSiteDetailsDestination(site: Site): SiteDetailsDestination {
  const { provider } = site;

  if (provider && site.source !== 'freshtrak') {
    const organizationId = provider.alternateIds?.organizationId;
    if (organizationId) {
      return { kind: 'legacyPantry', organizationId };
    }
    // Shouldn't happen per the endpoint's contract, but this is still a stabilizing endpoint --
    // fall back to the FreshTrak-style destination rather than a broken/missing id.
    logError(new Error(`Site ${site.siteId} has source=${site.source} but no alternateIds.organizationId`));
  }

  return { kind: 'freshTrakSite', siteId: site.siteId };
}

export function navigateToDestination(navigation: NavigationLike, destination: SiteDetailsDestination): void {
  if (destination.kind === 'legacyPantry') {
    navigation.navigate('Pantry', { id: destination.organizationId });
  } else {
    navigation.navigate('SiteDetails', { siteId: destination.siteId });
  }
}

export function navigateToSiteDetails(navigation: NavigationLike, site: Site): void {
  navigateToDestination(navigation, resolveSiteDetailsDestination(site));
}
